import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const FIELD_LABEL = {
  no_polisi: "No Polisi", jumlah: "Jumlah", nama_barang: "Nama Barang",
  no_so: "No SO", distrik: "Distrik", tanggal: "Tanggal",
};

/**
 * The submit-a-correction dialog extracted from MismatchCorrectionPanel to
 * cut its cyclomatic complexity. Renders side-by-side rows (SPB current vs
 * ERP realisasi vs user's proposed value) for each field flagged 'diff'.
 *
 * All state (proposed, reason) is owned by the parent — this component is
 * a controlled view.
 */
export default function MismatchSubmitDialog({
  open, onClose, spb, diffFields,
  proposed, onProposedChange,
  reason, onReasonChange,
  submitting, onSubmit,
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="mismatch-submit-dialog">
        <DialogHeader>
          <DialogTitle>Ajukan Koreksi Mismatch</DialogTitle>
          <DialogDescription>
            Hanya field yang di-flag mismatch yang bisa dikoreksi. Perubahan butuh persetujuan Super Admin sebelum disimpan.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {diffFields.map((f) => (
            <DiffFieldRow
              key={f}
              field={f}
              spb={spb}
              value={proposed[f] ?? ""}
              onChange={(v) => onProposedChange({ ...proposed, [f]: v })}
            />
          ))}
          <div>
            <Label className="text-xs uppercase tracking-widest">Alasan / Justifikasi *</Label>
            <Textarea
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              placeholder="Contoh: ERP report benar, entry SPB kena typo"
              rows={3}
              data-testid="mismatch-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Batal</Button>
          <Button
            onClick={onSubmit}
            disabled={submitting}
            data-testid="mismatch-submit-btn"
          >
            {submitting ? "Mengirim…" : "Ajukan Koreksi"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DiffFieldRow({ field, spb, value, onChange }) {
  const currentValue = field === "distrik"
    ? `${spb?.distrik_kode || ""} ${spb?.distrik_nama || ""}`.trim()
    : spb?.[field];
  const erpValue = spb?.realisasi?.[field];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end p-3 border border-border bg-background">
      <div className="text-xs">
        <div className="uppercase text-muted-foreground tracking-widest mb-1">SPB (sekarang)</div>
        <div className="font-mono text-sm">{String(currentValue ?? "-")}</div>
      </div>
      <div className="text-xs">
        <div className="uppercase text-muted-foreground tracking-widest mb-1">ERP (real)</div>
        <div className="font-mono text-sm">{String(erpValue ?? "-")}</div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest">
          {FIELD_LABEL[field]} (usulan)
        </Label>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`mismatch-input-${field}`}
        />
      </div>
    </div>
  );
}
